/**
 * LayoutElementRegistry — F12.H T12.H.7.
 *
 * Drugi, RÓWNOLEGŁY rejestr do `FieldRegistry`. Reprezentuje elementy
 * strukturalne / kontenery / interaktywne / treść / komunikaty w buildery —
 * wszystko co NIE jest polem (które ma wartość + onChange + validate).
 *
 * Decyzja architektoniczna (sesja 2026-05-11):
 *  - **NIE rozszerzamy** `FieldRegistry` o layout elementy — to dwie różne
 *    abstrakcje:
 *      - Pole = leaf z `value`/`onChange`/`validate`/`renderCell`/`defaultValue`
 *      - Element layoutu = container z `children`/`settings`/`mode`
 *    Wymuszanie wspólnego interfejsu zaśmieca FieldRegistry dead-fieldsami
 *    i ryzykuje regresją w 48 istniejących typach pól.
 *  - **Każdy renderer formularza** (canvas buildera, RecordFormPage,
 *    podgląd) dispatchuje per element:
 *      - `element.type === 'field'` → `FieldRegistry.renderInput(...)`
 *      - inny `type` → `LayoutElementRegistry.render({ element, children, mode })`
 *
 * Użycie (przykład):
 *   import { LayoutElementRegistry } from '@/components/fields/LayoutElementRegistry';
 *   import '@/components/modules/layout-elements'; // side-effect: rejestracja
 *
 *   const out = LayoutElementRegistry.render({
 *     element: { id: 'x', type: 'section', settings: { title: 'Dane' } },
 *     children: <div>...</div>,
 *     mode: 'form',
 *   });
 *
 * Co implementujemy w kolejnych taskach F12.H:
 *  - T12.H.2 — 7 elementów strukturalnych strony (Sidebar/Header/Footer/...)
 *  - T12.H.3 — 6 kontenerów (Section/Row/Card/Columns/Grid/Fieldset/Panel)
 *  - T12.H.4 — interaktywne (form-level tabs + nested tabs + Akordeon + Stepper)
 *  - T12.H.5 — treść statyczna (Heading/Text/RichContent/Quote/List/Code)
 *  - T12.H.6 — komunikaty inline (Alert info/warning/error/success)
 *
 * @see docs/phases/PHASE_12H_PLAN.md — T12.H.7 LayoutElementRegistry fundament
 */

import {
  cloneElement,
  type CSSProperties,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  getAnimationClass,
  getAnimationStyle,
  styleToCSS,
} from '@/components/module-builder/style-panel/styleToCSS';
import type { LayoutElementStyle } from '@/components/module-builder/style-panel/styleTypes';

import '@/components/module-builder/style-panel/styleAnimations.css';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Tryb renderowania elementu layoutu.
 *  - `form` — produkcyjny formularz (RecordFormPage / RecordFormModal),
 *            user wchodzi w pełną interakcję
 *  - `preview` — podgląd w buildery (toolbar „Podgląd"), interakcja
 *               działa ale dane są efemeryczne (nie zapisuje rekordu)
 *  - `builder` — canvas kreatora, element jest „elementem do edycji",
 *               kliknięcie zaznacza, drag działa, conditional visibility
 *               IGNOROWANA (zawsze renderuj)
 */
export type LayoutRenderMode = 'form' | 'preview' | 'builder';

/**
 * Bazowy kształt elementu layoutu — wspólny dla pól i typów strukturalnych.
 *
 * **`type`** to klucz w rejestrze: `'field'` → FieldRegistry, każdy inny
 * → LayoutElementRegistry.
 *
 * **`settings`** to luzny obiekt konfiguracji per typ (np. `{ title, columns }`
 * dla sekcji, `{ variant, content }` dla alertu) — trzymany w `layout_config`
 * JSONB. Każdy typ definiuje własny shape w `defaultSettings()`.
 *
 * **`children`** — opcjonalne ID dzieci (dla containerów typu section/tabs/akordeon).
 * Dzieci są referencjami do innych elementów w drzewie layoutu, nie inline'owane.
 *
 * **Conditional visibility** (`conditionalField`/`conditionalOperator`/
 * `conditionalValue`) — wspólne dla wszystkich elementów, ignorowane w trybie
 * `builder`. Ewaluator: `isLayoutElementVisible()` (planowany w T12.H.2 razem
 * z renderem rzeczywistych elementów).
 */
export interface LayoutElement {
  id: string;
  type: string;
  /** ID FK do `custom_module_fields` — wypełnione TYLKO dla `type === 'field'`. */
  fieldId?: string;
  /** ID dzieci elementu (dla containerów). */
  childrenIds?: string[];
  /** Width-w-siatce (1..N) — używane przez kontenery z `columns: N`. */
  width?: number;
  /** Konfiguracja specyficzna dla typu — kontrakt per typ definiowany przez `defaultSettings()`. */
  settings?: Record<string, unknown>;
  /** Conditional visibility — pole + operator + wartość docelowa. */
  conditionalField?: string | null;
  conditionalOperator?: 'equals' | 'not_equals' | 'empty' | 'not_empty' | null;
  conditionalValue?: string | null;
  /** Styl wizualny — opcjonalny, kontrakt z `style/styleTypes.ts`. */
  style?: Record<string, unknown>;
}

/**
 * Props przekazywane do funkcji `render()` typu layoutu.
 *
 * **`element`** — sam element (z settings + style + conditional).
 * **`children`** — już zrenderowane dzieci (`ReactNode[]`); dla leaf elementów
 * (Heading/Text/Alert/...) jest pustą tablicą.
 * **`mode`** — kontekst renderu (form/preview/builder).
 * **`formData`** — bieżące wartości formularza (potrzebne do conditional
 * visibility ewaluatora; przekazywane przez renderera, nie odczytywane
 * z kontekstu globalnego — chcemy żeby Registry był pure function).
 */
export interface LayoutElementRenderProps {
  element: LayoutElement;
  children: ReactNode[];
  mode: LayoutRenderMode;
  formData?: Record<string, unknown>;
  /**
   * Zapis PARTIAL `settings` elementu WPROST z podglądu (tylko `mode==='builder'`).
   * Domyślnie podgląd elementu jest pure/read-only (ustawienia zmienia się w
   * `propertiesEditor` w prawym panelu). Ten opcjonalny callback pozwala elementom
   * z kontrolkami inline (np. formatka „Adres macierzysty" / bloki kontrahenta z
   * ikonką oka do ukrywania pól) mutować `settings` bez wchodzenia w panel.
   * Builder wpina go w trybie `builder` (→ `onUpdateElementSettings(elementId, patch)`);
   * elementy, które go nie potrzebują, po prostu ignorują (wsteczna zgodność).
   */
  onSettingsChange?: (patch: Record<string, unknown>) => void;
  /**
   * Layout SYSTEMOWY (`is_system_managed`) w builderze: elementy z kontrolkami inline
   * (formatki) mają wtedy pokazywać TYLKO ikonkę oka (ukryj), a NIE uchwyt przeciągania —
   * zasada „systemowy = tylko ukrywanie, bez przesuwania". Dodatkowo canvas jest wtedy
   * inertny (pointerEvents:none), więc formatka musi „przebić" tę blokadę na samym oku
   * (patrz `pierceLock` w `FormatkaReorderList`). `false`/brak = własny layout (pełna edycja).
   */
  systemHideOnly?: boolean;
}

/**
 * Definicja typu layoutu — interfejs do rejestracji w singletonie.
 *
 * **`type`** — unikalny string identyfikator (np. `'section'`, `'tabs'`,
 * `'alert'`). MUSI być inny niż `'field'` (zarezerwowane dla FieldRegistry).
 *
 * **`render(props)`** — zwraca ReactNode. Implementacja KOMPONENTU
 * funkcyjnego (może używać hooków wewnętrznych — w przeciwieństwie do
 * `FieldRegistry.renderInput` które ma znany dług z hookami).
 *
 * **`defaultSettings()`** — zwraca domyślny shape settings dla NOWO
 * dodanego elementu (np. sekcja → `{ title: 'Nowa sekcja', columns: 1 }`).
 * Wywoływane przez builder gdy user przeciągnie typ z palety.
 *
 * **`category`** — opcjonalna kategoria do grupowania w palecie buildera
 * (`'struktura' | 'kontenery' | 'interaktywne' | 'tresc' | 'komunikaty'`).
 * Pozwala builderowi pokazać elementy w sekcjach (jak SellCall).
 *
 * **`isContainer`** — flaga: czy element może mieć dzieci. Wpływa na drag&drop
 * w buildery (containery są drop-targets).
 *
 * **`label`** — nazwa wyświetlana w palecie (z i18n key lub literal).
 *
 * **`icon`** — nazwa ikony Lucide (`'LayoutDashboard'`, `'Boxes'` itd.).
 */
/**
 * Props przekazywane do `propertiesEditor` typu (T12.H.9 sub-tab „Treść").
 *
 * Editor renderuje formularz do edycji `settings` elementu — np. dla Heading:
 * input na tekst + select na level. Dla Alert: select wariant + textarea
 * treści + toggle dismissible.
 *
 * `onChange` przyjmuje PARTIAL settings (merge w callerze) — pozwala
 * editorowi aktualizować tylko zmienione pola.
 */
export interface LayoutElementPropertiesEditorProps {
  settings: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  /**
   * Slug modułu systemowego, którego layout edytuje builder (np. `purchase_invoices`)
   * — potrzebny elementom, których editor musi filtrować katalog per moduł (np.
   * `additional_view`/`additional_query_view` — wybór tabeli/widoku dodatkowego
   * scopingowany przez `parentEntity`, mirror `AdditionalTableConfigPanel`).
   * Opcjonalny — większość editorów (Heading/Alert/Pozycje dokumentu) go nie potrzebuje.
   */
  moduleSlug?: string;
}

export interface LayoutElementDefinition {
  type: string;
  render: (props: LayoutElementRenderProps) => ReactNode;
  defaultSettings: () => Record<string, unknown>;
  /**
   * Editor właściwości tre­ści dla tego typu elementu — renderowany w
   * right panelu builera, sub-tab „Treść" (T12.H.9). Opcjonalny — jeśli
   * element ma `settings` które user może edytować (np. nagłówek/text/alert),
   * implementuje editor; kontenery które maja settings poprzez sekcje
   * struktury (columns/padding) — zostawiają puste, lub T12.H.10 dorzuci.
   */
  propertiesEditor?: (props: LayoutElementPropertiesEditorProps) => ReactNode;
  category?: 'struktura' | 'kontenery' | 'interaktywne' | 'tresc' | 'komunikaty';
  isContainer?: boolean;
  label?: string;
  /**
   * Klucz i18n nazwy typu — dla KOMUNIKATÓW kreatora (toast „drugi blok nie został dodany”):
   * tam nazwa ma iść przez tłumaczenia, a `label` to dosłowny polski napis. Brak = komunikat
   * używa `label` (a gdy i jego brak — samego `type`). Paleta nadal czyta `label`.
   */
  labelKey?: string;
  icon?: string;
  /**
   * Scoping opcjonalny — slugi modułów systemowych (`system_modules.slug`), dla
   * których ten element jest widoczny w palecie buildera. `undefined` (default)
   * = widoczny w KAŻDYM module (większość elementów strukturalnych/treści).
   * Ustaw gdy element ma sens TYLKO w konkretnych modułach (np.
   * `document_items_view` — tylko 6 modułów klasy Dokumenty; bez tego pola
   * placeholder pojawiał się też w builderze kontrahentów/pracowników/modułów
   * własnych, gdzie nie ma dla niego rendera na realnej stronie).
   * Filtrowane przez `LayoutElementsPalette` na podstawie propsa `moduleSlug`.
   */
  allowedModules?: string[];
  /**
   * Element renderuje się PEŁNĄ szerokością sekcji (col-span-full w gridzie canvasu
   * buildera), a nie jako pojedyncza komórka gridu. Dla bloków-formatek (np.
   * „Adres macierzysty", bloki kontrahenta z listą pól) — inaczej ściska je do pół
   * kolumny i pola wyglądają na malutkie. Mirror `md:col-span-2` na realnej karcie.
   */
  fullWidth?: boolean;
  /**
   * Ukryj z PALETY buildera, ale ZOSTAW zarejestrowany w runtime — element nie
   * daje się już dodać do nowych layoutów, a istniejące zapisane layouty z tym
   * typem nadal się renderują (zamiast placeholderu „nieznany typ"). Używane do
   * wycofywania elementów-atrap bez psucia zapisanych danych (np. `page-navbar`).
   * `LayoutElementsPalette` odfiltrowuje `hiddenFromPalette === true`.
   */
  hiddenFromPalette?: boolean;
  /**
   * Element VIEW-LEVEL (T2) — renderowany NAD paskiem zakładek, jako rodzeństwo
   * PRZED elementem `tabs` w `layoutConfig.elements`, a NIE w treści zakładki.
   * Builder wstawia go do slotu view-level (pomija sekcje/extras). Runtime mapuje
   * `elements` po kolejności, więc rodzeństwo przed `tabs` renderuje się nad
   * zakładkami bez zmian renderera. Np. `page-header`, `highlight`.
   */
  viewLevel?: boolean;
  /**
   * Tylko dla `viewLevel` — dopuszczalny JEDEN egzemplarz na widok (blokada
   * drugiego w palecie + defensywnie w handlerze). Np. `page-header`.
   * `highlight` jest view-level BEZ singletona (może być kilka).
   */
  viewLevelSingleton?: boolean;
  /**
   * Dopuszczalny JEDEN egzemplarz tego typu w CAŁYM layoucie — w dowolnym miejscu drzewa
   * (zakładki, sekcje i ich dzieci, klocki luźne zakładki-kontenera, panele akordeonu, kroki).
   * Szersze niż `viewLevelSingleton` (to dotyczy tylko rodzeństwa `tabs` nad zakładkami).
   * Po co: nakładka per-tenant na układzie SYSTEMOWYM (`elementOverrides`, klucz z
   * `elementOverrideKey`) ma dla tego typu jeden wpis na TYP elementu — dwa bloki tego samego
   * typu współdzieliłyby jedną maskę ukrytych kolumn. Builder wyklucza typ z palety, gdy jest
   * już w layoucie, i defensywnie odrzuca dodanie drugiego (toast). Np. `document_items_view`,
   * `vat_register_view`.
   */
  layoutSingleton?: boolean;
}

// ─── Registry singleton ─────────────────────────────────────────────────────

class LayoutElementRegistryClass {
  private types = new Map<string, LayoutElementDefinition>();

  register(definition: LayoutElementDefinition): void {
    if (definition.type === 'field') {
      throw new Error(
        '[LayoutElementRegistry] Typ "field" jest zarezerwowany dla FieldRegistry. ' +
          'Użyj innej nazwy (np. "field-display") jeśli potrzebujesz field-like elementu.',
      );
    }
    this.types.set(definition.type, definition);
  }

  get(type: string): LayoutElementDefinition | undefined {
    return this.types.get(type);
  }

  has(type: string): boolean {
    return this.types.has(type);
  }

  /** Wszystkie zarejestrowane typy (do palety buildera). */
  getAll(): LayoutElementDefinition[] {
    return Array.from(this.types.values());
  }

  /** Pogrupowane po kategorii (do palety SellCall-style). */
  getByCategory(
    category: NonNullable<LayoutElementDefinition['category']>,
  ): LayoutElementDefinition[] {
    return this.getAll().filter((d) => d.category === category);
  }

  /** Reset rejestru — używane TYLKO w testach. */
  _reset(): void {
    this.types.clear();
  }

  /**
   * Zwraca domyślne settings dla nowego elementu danego typu.
   * Używane przez builder przy drag-drop z palety.
   */
  defaultSettings(type: string): Record<string, unknown> {
    const def = this.get(type);
    return def?.defaultSettings() ?? {};
  }

  /**
   * Renderuje element przez zarejestrowaną definicję typu.
   * Gdy typ nieznany — zwraca placeholder fallback (czerwony box w mode
   * builder, NIC w mode form/preview — żeby nie zaśmiecać rekordu).
   */
  render(props: LayoutElementRenderProps): ReactNode {
    const def = this.get(props.element.type);
    if (!def) {
      return this.renderUnknown(props);
    }
    return applyElementStyle(def.render(props), props.element.settings);
  }

  private renderUnknown(props: LayoutElementRenderProps): ReactNode {
    if (props.mode !== 'builder') return null;
    return (
      <div
        className="rounded border border-dashed border-danger-300 bg-danger-50/50 p-2 text-xs text-danger-700"
        data-testid={`layout-element-unknown-${props.element.type}`}
      >
        Nieznany typ elementu layoutu: <code>{props.element.type}</code>
      </div>
    );
  }
}

/**
 * Nakłada styl użytkownika (`settings.style` z panelu stylów formatek) na
 * wyrenderowany element layoutu. Styl idzie jako INLINE `style` na root elementu,
 * więc **nadpisuje** klasy wariantu (np. kolory Ostrzeżenia w wyróżniku) — inline
 * ma wyższą specyficzność niż klasa Tailwind. Decyzja 2026-08-24: własny styl
 * wygrywa z wariantem.
 *
 * Bez tego rendery klocków (highlight/heading/alert/card/…) czytały tylko swoje
 * settings (wariant/tytuł/ikona) i całkowicie IGNOROWAŁY zapisany `style` — panel
 * stylów zapisywał do bazy, ale na kartotece nic nie było widać. Pola mają własną
 * ścieżkę stylu w RecursiveFormRenderer i tu NIE wchodzą (dispatch po `type==='field'`).
 *
 * Gdy root nie przyjmuje `style` (Fragment / null / string) — owijamy w `<div>`.
 */
function applyElementStyle(
  node: ReactNode,
  settings: Record<string, unknown> | undefined,
): ReactNode {
  const raw = (settings as { style?: LayoutElementStyle } | undefined)?.style;
  if (!raw) return node;
  const css = { ...styleToCSS(raw), ...getAnimationStyle(raw) };
  const animClass = getAnimationClass(raw);
  if (Object.keys(css).length === 0 && !animClass) return node;

  if (isValidElement(node) && node.type !== Fragment) {
    const el = node as ReactElement<{ style?: CSSProperties; className?: string }>;
    return cloneElement(el, {
      style: { ...el.props.style, ...css },
      className: [el.props.className, animClass].filter(Boolean).join(' ') || undefined,
    });
  }
  return (
    <div className={animClass || undefined} style={css}>
      {node}
    </div>
  );
}

export const LayoutElementRegistry = new LayoutElementRegistryClass();

// ─── Helpers (dla renderów formularzy) ──────────────────────────────────────

/**
 * Dispatch helper: bierze element layoutu i decyduje czy delegować do
 * `FieldRegistry` (gdy `type === 'field'`) czy `LayoutElementRegistry`.
 *
 * **NIE używamy** bezpośrednio w T12.H.7 — to gotowy contract dla T12.H.2
 * gdy zaczniemy refactor `RecordFormModal` / `RecordDetailPage` /
 * `ModuleBuilderPage` (3 callsity z duplicated dispatch). Wtedy każdy z tych
 * plików zamiast hardcoded `if (element.type === 'field') ...` wywoła
 * jedną funkcję `dispatchLayoutElement(...)`.
 *
 * Sygnatura `FieldRegistry.renderInput` jest inna (przyjmuje `field` +
 * `value` + `onChange`), więc funkcja TYLKO routuje do
 * `LayoutElementRegistry.render` — `field` dispatch zostaje w handlerze
 * caller'a (caller musi znać `formData` per field slug).
 *
 * Zwraca `null` gdy element jest typu `field` — wtedy caller użyje
 * `FieldRegistry.renderInput` z odpowiednimi propsami.
 */
export function tryRenderLayoutElement(props: LayoutElementRenderProps): ReactNode | null {
  if (props.element.type === 'field') return null;
  return LayoutElementRegistry.render(props);
}

/**
 * Ewaluator conditional visibility — wspólny dla pól i layout elementów.
 * Sprawdza pole `element.conditionalField` + operator + wartość vs `formData`.
 *
 * Zwraca `true` gdy element JEST widoczny (lub gdy brak warunku).
 * W trybie `builder` zawsze zwraca `true` (user widzi wszystkie elementy
 * niezależnie od warunków, żeby móc je edytować).
 */
export function isLayoutElementVisible(
  element: LayoutElement,
  formData: Record<string, unknown>,
  mode: LayoutRenderMode = 'form',
): boolean {
  if (mode === 'builder') return true;

  const { conditionalField, conditionalOperator, conditionalValue } = element;
  if (!conditionalField || !conditionalOperator) return true;

  const rawValue = formData[conditionalField];
  const strValue = rawValue != null ? String(rawValue) : '';
  const isEmpty =
    rawValue == null || strValue === '' || (Array.isArray(rawValue) && rawValue.length === 0);

  switch (conditionalOperator) {
    case 'empty':
      return isEmpty;
    case 'not_empty':
      return !isEmpty;
    case 'equals':
      return strValue === (conditionalValue ?? '');
    case 'not_equals':
      return strValue !== (conditionalValue ?? '');
    default:
      return true;
  }
}
